import { PayloadAction } from '@reduxjs/toolkit';
import { SagaIterator } from '@redux-saga/types';
import { call, put } from 'redux-saga/effects';

import {
  amlbotAPI,
  EnterPromoCodeRequest,
  GetPriceListRequest,
  GetPriceListResponse,
  InvoiceRequest,
} from '@/api';

import { paymentActions, userActions } from '@/redux/actions';
import { paymentMappers } from '@/redux/mappers';
import { parseError } from '@/redux/helpers';

function* fetchPriceListPlans(
  action: PayloadAction<GetPriceListRequest>,
): SagaIterator {
  try {
    const response: GetPriceListResponse = yield call(amlbotAPI.fetchPriceList);
    const data = paymentMappers.fetchPriceListPlans(response);

    yield put(paymentActions.setPaymentPlans(data));
    yield put(paymentActions.setBiggestPricePerCheck(data));
  } catch (error: unknown) {
    // console.log(error);
  } finally {
    yield put(paymentActions.setLoading(false));
  }
}

function* fetchInvoiceHistory(): SagaIterator {
  try {
    const response = yield call(amlbotAPI.fetchInvoiceHistory, {});
    const data = paymentMappers.fetchInvoiceHistory(response);
    yield put(paymentActions.setInvoiceHistory(data));
    yield put(userActions.setBalance(data.balance));
  } catch (error) {
    const { message } = parseError(error);
    yield put(paymentActions.setInvoiceHistoryError(message));
  } finally {
    yield put(paymentActions.setInvoiceHistoryLoading(false));
  }
}

function* enterPromoCode(
  action: PayloadAction<EnterPromoCodeRequest>,
): SagaIterator {
  try {
    yield put(paymentActions.setEnterPromoSuccess(false));
    yield put(paymentActions.setEnterPromoLoading(true));
    yield put(paymentActions.setEnterPromoError(null));
    const { payload } = action;
    const response = yield call(amlbotAPI.enterPromoCode, payload);
    const data = paymentMappers.enterPromoCode(response);
    yield put(paymentActions.setEnterPromoSuccess(true));
    yield put(userActions.setBalance(data.balance));
  } catch (error) {
    const { message, errorCode } = parseError(error);
    // Optionally handle errorCode - ErrorCodeEnum.FIELD_REQUIRED
    yield put(paymentActions.setEnterPromoError(message));
  } finally {
    yield put(paymentActions.setEnterPromoLoading(false));
  }
}

function* createStripeInvoice(
  action: PayloadAction<InvoiceRequest>,
): SagaIterator {
  try {
    const response = yield call(amlbotAPI.createStripeInvoice, action.payload);
    const data = paymentMappers.createStripeInvoice(response);
    yield put(paymentActions.setStripeKey(data.data));
  } catch (error: unknown) {
    const { message, errorCode } = parseError(error);
    // Optionally handle errorCode - ErrorCodeEnum.FIELD_REQUIRED
    yield put(paymentActions.setError(message));
    yield put(paymentActions.setPaymentLoading(false));
  }
}

function* createCryptoInvoice(
  action: PayloadAction<InvoiceRequest>,
): SagaIterator {
  try {
    yield put(paymentActions.setCryptoInvoiceError(null));
    const response = yield call(amlbotAPI.createCryptoInvoice, action.payload);
    const data = paymentMappers.createCryptoInvoice(response);
    yield put(paymentActions.setCryptoInvoice(data));
  } catch (error) {
    const { message, errorCode } = parseError(error);

    // Optionally handle errorCode - ErrorCodeEnum.FIELD_REQUIRED
    yield put(paymentActions.setCryptoInvoiceError(message));
  } finally {
    yield put(paymentActions.setPaymentLoading(false));
  }
}

export {
  fetchPriceListPlans,
  enterPromoCode,
  createStripeInvoice,
  createCryptoInvoice,
  fetchInvoiceHistory,
};
